import { useState, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"

export const categories = [
    { label: "Acoustic Guitar", value: "ACOUSTIC_GUITAR_GROUP" },
    { label: "Bass", value: "BASS_GROUP" },
    { label: "Brass", value: "BRASS_GROUP" },
    { label: "Backing Track", value: "BACKING_TRACK_GROUP" },
    { label: "Backing Vox", value: "BACKING_VOX_GROUP" },
    { label: "Cymbals", value: "CYMBALS_GROUP" },
    { label: "Drums", value: "DRUMS_GROUP" },
    { label: "Electric Guitar", value: "E_GUITAR_GROUP" },
    { label: "FX", value: "FX_GROUP" },
    { label: "Keys", value: "KEYS_GROUP" },
    { label: "Kick", value: "KICK_GROUP" },
    { label: "Percussion", value: "PERCS_GROUP" },
    { label: "Snare", value: "SNARE_GROUP" },
    { label: "Strings", value: "STRINGS_GROUP" },
    { label: "Synth", value: "SYNTH_GROUP" },
    { label: "Vocals", value: "VOCAL_GROUP" },
    { label: "Other", value: "OTHER_GROUP1" },
]

export const CategoryComboBox = ({ value, onChange, categories: propCategories }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    })

    const buttonRef = useRef(null)

    const options =
        Array.isArray(propCategories) && propCategories.length
            ? propCategories
            : categories

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const selectedLabel =
        options.find((o) => o.value === value)?.label || value || "Select"

    const calculatePosition = useCallback(() => {
        if (!buttonRef.current) return

        const rect = buttonRef.current.getBoundingClientRect()
        const dropdownHeight = 258

        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top

        let top

        if (spaceBelow >= dropdownHeight) {
            top = rect.bottom + 8
        } else if (spaceAbove >= dropdownHeight) {
            top = rect.top - dropdownHeight - 8
        } else {
            top =
                spaceBelow > spaceAbove
                    ? rect.bottom + 8
                    : rect.top - dropdownHeight - 8
        }

        setDropdownPosition({
            top,
            left: rect.left,
            width: Math.max(rect.width, 200),
        })
    }, [])

    useEffect(() => {
        if (isOpen) {
            calculatePosition()
        }
    }, [isOpen, calculatePosition])

    useEffect(() => {
        if (!isOpen) return

        const handleScroll = () => calculatePosition()
        const handleResize = () => calculatePosition()

        window.addEventListener("scroll", handleScroll, true)
        window.addEventListener("resize", handleResize)

        return () => {
            window.removeEventListener("scroll", handleScroll, true)
            window.removeEventListener("resize", handleResize)
        }
    }, [isOpen, calculatePosition])

    const handleClose = () => {
        setIsOpen(false)
        setSearchTerm("")
    }

    const dropdown =
        isOpen &&
        createPortal(
            <>
                <div
                    className="fixed inset-0 z-[9998]"
                    onClick={handleClose}
                />
                <div
                    className="fixed z-[9999] bg-theme_clr1 rounded-xl shadow-lg"
                    style={{
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`,
                    }}
                >
                    <div className="p-2 border-b border-theme_clr2/30">
                        <input
                            type="text"
                            placeholder="Search category..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-theme_clr2 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                        />
                    </div>

                    <div className="max-h-[200px] overflow-y-auto overflow-x-hidden">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => {
                                        onChange(option.value)
                                        handleClose()
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-white text-sm hover:bg-theme_clr2 transition-colors whitespace-nowrap overflow-hidden text-ellipsis ${
                                        value === option.value
                                            ? "bg-theme_clr2"
                                            : ""
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-3 text-white/60 text-sm">
                                No categories found
                            </div>
                        )}
                    </div>
                </div>
            </>,
            document.body
        )

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setIsOpen((prev) => !prev)}
                className="bg-black/25 px-3 py-2 rounded-full text-xs text-white flex items-center gap-2 min-w-[100px] justify-between"
            >
                <span>{selectedLabel}</span>
                <svg
                    className={`w-4 h-4 transition-transform ${
                        isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>
            {dropdown}
        </div>
    )
}
